<template>
  <div>
    <component :is="embedded ? 'div' : 'b-container'" v-bind="embedded ? {} : { fluid: true }">
      <div :class="embedded ? '' : 'iq-card mt-3 px-3'">
        <div :class="embedded ? 'd-flex justify-content-end mb-2' : 'iq-card-header d-flex justify-content-between'">
          <template v-if="!embedded">
            <div class="iq-header-title py-3">
              <h4 class="card-title">{{ title }}</h4>
            </div>
            <b-row>
              <b-col md="12">
                <div class="text-right">
                  <slot name="actions" />
                  <button-options
                    :access="access"
                    :hideAdd="!allowCreate"
                    :hideDetail="true"
                    :isRefreshEnabled="true"
                    :isRefreshed="isRefreshed"
                    @onAddItemClicked="openAddForm"
                    @onRefreshed="getData('Refreshed')"
                  />
                </div>
              </b-col>
            </b-row>
          </template>
          <template v-else>
            <slot name="actions" />
            <button-options
              :access="access"
              :hideAdd="!allowCreate"
              :hideDetail="true"
              :isRefreshEnabled="true"
              :isRefreshed="isRefreshed"
              @onAddItemClicked="openAddForm"
              @onRefreshed="getData('Refreshed')"
            />
          </template>
        </div>
        <div v-if="$slots.intro" :class="embedded ? 'pb-2 text-muted small' : 'px-3 pb-2 text-muted small'">
          <slot name="intro" />
        </div>
        <div :class="embedded ? '' : 'iq-card-body'">
          <b-row v-if="filters.length">
            <b-col v-for="entry in filters" :key="entry.key" :md="entry.colMd || 4">
              <label :for="entry.key" class="m-0">{{ entry.label }}:</label>
              <v-select
                v-if="entry.type === 'select' && entry.searchable"
                :id="entry.key"
                v-model="filterValues[entry.key]"
                :options="entry.options || filterSourceOptions[entry.key] || []"
                label="text"
                :reduce="(option) => option.value"
                clearable
                placeholder="All"
                class="mb-2 searchable-select-sm"
                @input="onFilterChanged"
              />
              <b-form-select
                v-else-if="entry.type === 'select'"
                :id="entry.key"
                v-model="filterValues[entry.key]"
                :options="filterOptionsFor(entry)"
                class="mb-2"
                size="sm"
                @change="onFilterChanged"
              />
              <b-form-input
                v-else
                :id="entry.key"
                v-model="filterValues[entry.key]"
                class="mb-2"
                size="sm"
                debounce="400"
                @update="onFilterChanged"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col md="12">
              <master-table
                :access="access"
                :columns="columns"
                :rows="data"
                :loading="isLoading"
                :pagination="pagination"
                :empty-text="accessMessage || 'No data available.'"
                :page-size-selectable="pageSizeSelectable"
                :page-size-options="pageSizeOptions"
                :allow-delete="allowDelete"
                @edit="openEdit"
                @delete="openDelete"
                @page-change="onPageChanged"
                @page-size-change="onPageSizeChanged"
              >
                <template v-if="$scopedSlots['row-actions']" #row-actions="{ row }">
                  <slot name="row-actions" :row="row" />
                </template>
              </master-table>
            </b-col>
          </b-row>
        </div>
        <master-form-modal
          :visible="displayForm"
          :title="title"
          :fields="fields"
          :value="current"
          :errors="errors"
          :isSubmit="isSubmit"
          :pkField="pk"
          @update:visible="displayForm = $event"
          @submit="onSubmitClicked"
          @cancel="onCancel"
        />
        <b-modal size="lg" hide-footer no-close-on-esc no-close-on-backdrop v-model="displayDeletion">
          <delete-item
            :isSubmit="isSubmit"
            @onCancelDeletion="displayDeletion = false"
            @onConfirmDeletion="onConfirmDeletion"
          />
        </b-modal>
      </div>
    </component>
  </div>
</template>

<script>
import ButtonOptions from '@/components/directives/button-options';
import DeleteItem from '@/components/directives/delete-item';
import MasterTable from '@/components/master/MasterTable';
import MasterFormModal from '@/components/master/MasterFormModal';
import { createMasterCrud, isOk, bodyMessage } from '@/services/masterCrud';
import { buildMasterParams, readPagination, PAGE_SIZE_OPTIONS } from '@/util/master/masterQuery';
import { fieldErrors } from '@/util/master/masterErrors';
import { bodyStatusError } from '@/util/master/requestError';

export default {
  name: 'MasterCrudPage',
  components: {
    ButtonOptions, DeleteItem, MasterTable, MasterFormModal,
  },
  props: {
    master: { type: String, required: true },
    title: { type: String, required: true },
    access: { type: String, required: true },
    pk: { type: String, required: true },
    columns: { type: Array, required: true },
    fields: { type: Array, required: true },
    filters: { type: Array, default: () => [] },
    pageSize: { type: Number, default: 10 },
    pageSizeSelectable: { type: Boolean, default: false },
    forbiddenText: { type: String, default: 'You do not have access to this data.' },
    embedded: { type: Boolean, default: false },
    allowCreate: { type: Boolean, default: true },
    allowDelete: { type: Boolean, default: true },
    initialFilters: { type: Object, default: () => ({}) },
  },
  data() {
    // Semua key filter dideklarasiin sekarang, bukan pas dipakai.
    const filterValues = {};
    this.filters.forEach((entry) => {
      filterValues[entry.key] = this.initialFilters[entry.key] ?? null;
    });

    return {
      accessMessage: '',
      crud: createMasterCrud(this.master),
      current: {},
      data: [],
      displayDeletion: false,
      displayForm: false,
      errors: {},
      filterSourceOptions: {},
      filterValues,
      isLoading: false,
      isRefreshed: false,
      isSubmit: false,
      selected: null,
      pagination: { current: 1, pageSize: this.pageSize, totalSize: 0 },
    };
  },
  computed: {
    pageSizeOptions() {
      const hasPageSize = PAGE_SIZE_OPTIONS.some((option) => option.value === this.pageSize);
      if (hasPageSize) return PAGE_SIZE_OPTIONS;
      return [{ value: this.pageSize, text: `${this.pageSize} / page` }, ...PAGE_SIZE_OPTIONS];
    },
  },
  methods: {
    filterOptionsFor(entry) {
      const base = entry.options || this.filterSourceOptions[entry.key] || [];
      return [{ value: null, text: 'All' }].concat(base);
    },
    async loadFilterOptions() {
      const pending = this.filters.filter((entry) => entry.source);
      if (!pending.length) return;

      const loaded = { ...this.filterSourceOptions };
      await Promise.all(pending.map(async (entry) => {
        const { master, keyField, displayField } = entry.source;
        try {
          const body = await createMasterCrud(master).list(buildMasterParams({ page: 1, max: 1000 }));
          const rows = Array.isArray(body.data) ? body.data : [];
          loaded[entry.key] = rows.map((row) => ({ value: row[keyField], text: row[displayField] }));
        } catch (e) {
          loaded[entry.key] = [];
        }
      }));
      this.filterSourceOptions = loaded;
    },
    async getData(notification) {
      try {
        this.isLoading = true;
        this.isRefreshed = true;
        const body = await this.crud.list(buildMasterParams({
          page: this.pagination.current,
          max: this.pagination.pageSize,
          finds: this.filterValues,
        }));
        // Beberapa modul balikin 412 (HTTP 200) walau permission-nya ada.
        const failure = bodyStatusError(body);
        this.accessMessage = failure && failure.errorCode === 412 ? this.forbiddenText : '';
        this.data = Array.isArray(body.data) ? body.data : [];
        this.pagination.totalSize = readPagination(body).totalSize;
        if (notification) this.$toast.success(notification);
      } catch (e) {
        console.error('get data error : ', e);
        this.$toast.error('Something wrong when fetching data');
      } finally {
        this.isLoading = false;
        this.isRefreshed = false;
      }
    },
    onFilterChanged() {
      this.pagination.current = 1;
      this.getData();
    },
    onPageChanged(page) {
      this.pagination.current = page;
      this.getData();
    },
    onPageSizeChanged(size) {
      this.pagination.pageSize = size;
      this.pagination.current = 1;
      this.getData();
    },
    openAddForm() {
      this.errors = {};
      this.current = {};
      this.displayForm = true;
    },
    openEdit(row) {
      this.errors = {};
      this.current = { ...row };
      this.displayForm = true;
    },
    openDelete(row) {
      this.selected = row;
      this.displayDeletion = true;
    },
    onCancel() {
      this.current = {};
      this.displayForm = false;
    },
    onConfirmDeletion() {
      this.deleteData();
    },
    async deleteData() {
      this.isSubmit = true;
      try {
        const body = await this.crud.remove(this.selected[this.pk]);
        if (isOk(body)) {
          this.$toast.success(`${this.title} deleted`);
          this.displayDeletion = false;
          this.pagination.current = 1;
          this.getData();
        } else {
          this.$toast.error(bodyMessage(body) || 'Error when deleting data');
        }
      } catch (e) {
        this.$toast.error(e.message);
      } finally {
        this.isSubmit = false;
      }
    },
    async onSubmitClicked(payload) {
      this.errors = {};
      this.isSubmit = true;
      try {
        const id = this.current[this.pk];
        const body = id
          ? await this.crud.update(id, payload)
          : await this.crud.create(payload);

        if (isOk(body)) {
          this.displayForm = false;
          this.current = {};
          await this.getData('Success');
        } else {
          // Modal sengaja dibiarin kebuka biar error nempel di fieldnya.
          this.errors = fieldErrors(body.status.message);
          this.$toast.error(bodyMessage(body) || 'Error when saving data');
        }
      } catch (e) {
        console.error('onSubmitClicked error : ', e);
        this.$toast.error('Error when submiting data');
      } finally {
        this.isSubmit = false;
      }
    },
  },
  mounted() {
    this.loadFilterOptions();
    this.getData();
  },
};
</script>

<style scoped src="./searchableSelectSm.css"></style>
